import React, { PureComponent } from 'react'
import styled, { keyframes } from 'styled-components'
import Dashboard, { ISkin as IDashboardSkin } from '../dashboard/Dashboard'
import Question, { ISkin as IQuestionSkin } from '../question/Question'
import Countdown, { ISkin as ICountdownSkin } from '../countdown/Countdown'
import { IMountainPlayerData } from '../../helpers/data/mountainPlayer'
import { IMountainSortedPlayer } from '../../helpers/data/mountainSortedPlayer'
import { IPlayerData } from '../../helpers/data/player'
import { IAudioData } from '../../helpers/data/audioData'
import { ISize } from '../../helpers/data/size'
import { IGameAnswer, IGameActivity, AnswerType, IGameAnswerMediaText, IGameAnswerMediaImage } from '../../helpers/data/question'
import { joinRichTextContent } from '../../helpers/html/richTextContent'
import { ReactComponent as PauseIcon } from '../../assets/icons/pause.svg'
import minQueryHelper from '../../helpers/minQueryHelper'
import gameStates from '../../helpers/enums/gameStates'
import getPalette, { IPalette } from '../../helpers/colors/colors'
import { IAccessibilityStore } from '../../redux/reducers/accessibilityReducer'

import AudioElement from '../../components/audio/AudioElement'
import ImagePreview from '../../components/imagePreview/ImagePreview'
import Timer from '../../components/timer/Timer'
import TopInformation from '../../components/topInformation/TopInformation'
import LoadingWheel from '../../components/loadingWheel/LoadingWheel'
import timings from '../../helpers/timings/timings'
import settingsEnums from '../../helpers/enums/settings'
import getFont from '../../helpers/fonts/fonts'
import VolumeBar from '../teacherGame/components/VolumeControl'
import AnimationsToggle from '../teacherGame/components/AnimationsToggle'
import wasFullyCorrectAnswered from '../../helpers/utils/wasFullyCorrectAnswered'
import { Button } from '@nearpod-packages/base-library';


const palette: IPalette = getPalette()

const scaleAnimationIn = keyframes`
  0% {
    transform: scale(0, 1);
    animation-timing-function: ease-out;
  }
  100% {
    transform: scale(1, 1);
  }
`

const waitingBarIn = keyframes`
  0% {
    transform: translateY(100%);
    animation-timing-function: ease-out;
  }
  100% {
    transform: translateY(0);
  }
`

const verticalTranslationIn = keyframes`
	0% {
		transform: translateY(-100%);
	}
	100% {
		transform: translateY(0);
	}
`

const Wrapper = styled.div`
	width: 100%;
	height: 100%;
	margin: auto;
	max-width: 1024px;
	max-height: 768px;
	overflow: hidden;
	position: absolute;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	@media (max-width: ${minQueryHelper}px) {
		max-height: none;
	}
`

const GameContainer = styled.div`
	width: 100%;
	height: calc(100% - 68px);
	display: block;
	position: relative;
	@media (max-width: ${minQueryHelper}px) {
		width: 100%;
	}
`
const DashboardContainer = styled.div`
	height: 100%;
	display: inline-block;
	vertical-align: top;
	position: relative;
	overflow: hidden;
	@media (max-width: ${minQueryHelper}px) {
		display: none;
	}
`

const DashboardContainerPausedOverlay = styled.div`
	height: 100%;
	width: 100%;
	position: absolute;
	top: 0;
	left: 0;
	background-color: rgba(0, 0, 0, 0.65);
	display: flex;
	justify-content: center;
	align-items: center;
	transition: all 2s ease 7s;
	animation: ${verticalTranslationIn} 1s;
	animation-delay: 7s;
	animation-fill-mode: both;
`

const PausedOverlayContent = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	color: white;
`

const PausedOverlayText = styled.div`
	padding-top: 16px;
	font-size: 24px;
	color: white;
	font-family: ${getFont('secondaryRegular')};
`

const QuestionContainer = styled.div`
	height: 100%;
	width: 512px;
	background-color: ${palette.gray};
	display: inline-block;
	vertical-align: top;
	@media (max-width: ${minQueryHelper}px) {
		width: 100%;
	}
`
const CountdownContainer = styled.div`
	width: 100%;
	height: 100%;
	background-color: white;
	position: absolute;
	top: 0;
	left: 0;
	z-index: 400;
`
const GameTimerContainer = styled.div`
	width: 90px;
	height: 90px;
	position: absolute;
	top: calc(40px + 68px);
	@media (max-width: ${minQueryHelper}px) {
		left: auto;
	}
`

const TopInformationContainer = styled.div`
	width: 100%;
	position: absolute;
	top: 0;
	left: 0;
`

const QuestionHeader = styled.div`
	width: 100%;
	height: 68px;
	color: ${palette.white};
	font-size: 28px;
	line-height: 32px;
	text-align: center;
	padding-top: 14px;

	h2 {
		margin: 0;
		padding: 0;
		font-size: inherit;
		font-weight: inherit;
		color: inherit;
		line-height: inherit;
		text-align: inherit;
	}
`

const QuestionHeaderPausedText = styled.div`
	position: relative;
	top: -17px;
	animation: ${scaleAnimationIn} 1s;
	animation-delay: 7s;
	animation-fill-mode: both;
`

const QuestionProgressBar = styled.div`
	width: 100%;
	height: 3%;
	animation: ${scaleAnimationIn} ${timings.questionTransition}s;
	animation-delay: ${timings.questionReveal}s;
	animation-fill-mode: both;
	transform-origin: 0 0;
	position: absolute;
	bottom: 0;
	left: 0;
`

const QuestionWaitingBar = styled.div`
	width: 100%;
	height: 61px;
	animation: ${waitingBarIn} 1s;
	animation-delay: 0.5s;
	animation-fill-mode: both;
	background-color: ${palette.primary};
	color: ${palette.white};
	display: flex;
	font-size: 24px;
	position: absolute;
	bottom: 0;
	left: 0;
	z-index: 190;
	@media (max-width: ${minQueryHelper}px) {
		font-size: 14px;
	}
`

const LoadingContainer = styled.div`
	width: 85%;
	height: 100%;
	padding: 23px;
	display: inline-block;
	@media (max-width: ${minQueryHelper}px) {
		padding: 23px 10px;
	}
`

const LoadingAnimation = styled.div`
	width: 16px;
	height: 16px;
	display: inline-block;
	@media (max-width: ${minQueryHelper}px) {
		width: 12px;
		height: 12px;
	}
`

const FloatingButton = styled.div`
	@media (max-width: ${minQueryHelper}px) {
		display: none;
	}
`

const GameControlsContainer = styled.div`
	display: flex;
	gap: 4px;
	position: absolute;
	bottom: 15px;
	right: 15px;
	z-index: 199;

	@media (max-width: ${minQueryHelper}px) {
		top: 50px;
		bottom: auto;
		right: 36px;
		justify-content: end;
		flex-wrap: wrap;
		max-width: 160px;
	}
`

export interface ISkin {
	audio: IAudioData
	dashboard: IDashboardSkin
	countdown: ICountdownSkin
	questions: IQuestionSkin
	progressBar: IProgressBarSkin
	topInformation: ITopInformationSkin
}

interface IProgressBarSkin {
	color: string
}

interface ITopInformationSkin {
	backgroundColor: string
}

interface IGameProps {
	answerSelected: (selectedAnswersIDs: string[], time: number, playerUid: string) => void
	isFinished: boolean
	playerData: IPlayerData
	players: IMountainPlayerData[]
	activity: IGameActivity
	totalActivities: number
	totalTime: number
	size: ISize
	elapsedTime: number
	skin: ISkin
	gameMode: number
	isPauseEnabledSetting: boolean
	isTimerDisabledSetting: number
	audioUpdate: (volume: number, isMuted: boolean) => void
	isSoundEnabledSetting: boolean
	volume: number
	isMuted: boolean
	accessibility: IAccessibilityStore
	currentPlayerRank: number
	sortedPlayers: IMountainSortedPlayer[]
	areAnimationsEnabled: boolean
	animationsToggleUpdate: (enabled: boolean) => void
}

interface IGameState {
	countdownComplete: boolean
	hasJoinedLate: boolean
	isImagePreviewOpen: boolean
	correctAnswers: IGameAnswer[]
	selectedAnswersIDs: string[] | null
}

class Game extends PureComponent<IGameProps, IGameState> {
	private elapsedTime = 0

	constructor(props: IGameProps) {
		super(props)
		const playerViewPayload = props.playerData.view.payload
		const isAnswered = playerViewPayload.state !== gameStates.WAITING
		this.state = {
			countdownComplete: isAnswered,
			hasJoinedLate: isAnswered && (!playerViewPayload.answersUids || !playerViewPayload.answersUids[0]),
			isImagePreviewOpen: false,
			correctAnswers: this.getCorrectAnswers(),
			selectedAnswersIDs: null,
		}
	}

	getCorrectAnswers() {
		const allAnswers = this.props.activity.payload.answers

		const correctAnswers = allAnswers.filter((answer: IGameAnswer) => {
			if (answer.correct) {
				return answer
			}
		})

		return correctAnswers
	}

	componentDidUpdate(prevProps: IGameProps) {
		if (prevProps.activity.index !== this.props.activity.index) {
			this.setState({
				countdownComplete: false,
				hasJoinedLate: false,
				isImagePreviewOpen: false,
				correctAnswers: this.getCorrectAnswers(),
				selectedAnswersIDs: null,
			})
		}
	}

	onComplete = () => {
		this.setState({
			countdownComplete: true,
		})
	}

	onTimeComplete = () => {
		const playerViewPayload = this.props.playerData.view.payload
		if (playerViewPayload.state === gameStates.WAITING) {
			this.props.answerSelected(
				this.state.selectedAnswersIDs || [''],
				this.props.totalTime / 1000,
				this.props.playerData.deviceUid
			)
		}
	}

	onTimeUpdate = (elapsedTime: number) => {
		//TODO: decide how to store this updated value
		this.elapsedTime = elapsedTime
	}

	onAnswerSelected = (answerId: string) => {
		const isMultipleAnswer = this.state.correctAnswers && this.state.correctAnswers.length >= 2
		//keep track of the new selectedAnswersID
		if (this.state.selectedAnswersIDs && isMultipleAnswer) {
			if (!this.state.selectedAnswersIDs.includes(answerId)) {
				//if the answerId was not already included in the list

				this.setState(
					{
						selectedAnswersIDs: [...this.state.selectedAnswersIDs, answerId],
					},
					() => {
						this.dispatchAnswerSelectedIfNeeded()
					}
				)
			}
		} else {
			this.setState(
				{
					selectedAnswersIDs: [answerId],
				},
				() => {
					this.dispatchAnswerSelectedIfNeeded()
				}
			)
		}
	}

	dispatchAnswerSelectedIfNeeded() {
		const isOwnPacedWithTimerDisabled =
			this.props.gameMode === settingsEnums.SETTING_GAME_MODE_OWN_PACED &&
			[settingsEnums.SETTING_TIMER_DISABLED_FROM_SESSION, settingsEnums.SETTING_TIMER_DISABLED_FROM_CREATION].includes(this.props.isTimerDisabledSetting)


		if (isOwnPacedWithTimerDisabled) {
			return
		}

		if (this.state.correctAnswers.length === (this.state.selectedAnswersIDs && this.state.selectedAnswersIDs.length)) {
			const playerViewPayload = this.props.playerData.view.payload

			if (playerViewPayload.state === gameStates.WAITING) {
				this.props.answerSelected(
					this.state.selectedAnswersIDs || [''],
					this.elapsedTime / 1000,
					this.props.playerData.deviceUid
				)
			}
		}
	}

	onManualSubmit = () => {
		const playerViewPayload = this.props.playerData.view.payload
		if (playerViewPayload.state === gameStates.WAITING) {
			this.props.answerSelected(
				this.state.selectedAnswersIDs || [''],
				this.elapsedTime / 1000,
				this.props.playerData.deviceUid
			)
		}
	}


	handleNextQuestionKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault()
			this.onManualSubmit()
		}
	}

	getGameSize(windowSize: ISize) {
		let gameWidth
		let questionWidth
		let gameHeight
		let dashboardWidth, dashboardHeight, scale
		if (windowSize.width < minQueryHelper) {
			return {
				gameWidth: '100%',
				gameHeight: '100%',
				dashboardWidth: '50%',
				dashboardHeight: '100%',
				questionWidth: '100%',
				scale: 1,
				timerScale: 0.7,
				timerRight: '30px',
				width: Number.MAX_SAFE_INTEGER,
				height: Number.MAX_SAFE_INTEGER,
			}
		} else {
			const originalWidth = 512
			const originalHeight = 700
			const containerHeight = Math.min(windowSize.height - 68, originalHeight)
			const containerWidth = Math.min(windowSize.width - 512, originalWidth)
			const containerRel = containerWidth / containerHeight
			const originalRel = originalWidth / originalHeight
			if (originalRel > containerRel) {
				dashboardHeight = (originalHeight * containerWidth) / originalWidth
				dashboardWidth = (dashboardHeight * originalWidth) / originalHeight
			} else {
				dashboardWidth = (originalWidth * containerHeight) / originalHeight
				dashboardHeight = (dashboardWidth * originalHeight) / originalWidth
			}
			gameWidth = Math.min(1024, windowSize.width)
			gameHeight = dashboardHeight + 68
			questionWidth = gameWidth - dashboardWidth
			scale = dashboardWidth / originalWidth
		}
		return {
			width: gameWidth,
			height: gameHeight,
			gameWidth: gameWidth + 'px',
			dashboardWidth: dashboardWidth + 'px',
			dashboardHeight: dashboardHeight + 'px',
			gameHeight: gameHeight + 'px',
			questionWidth: questionWidth + 'px',
			scale,
			timerScale: scale,
			timerRight: dashboardWidth - 150 * scale + 'px',
		}
	}

	toggleImagePreview = () => {
		this.setState({
			isImagePreviewOpen: !this.state.isImagePreviewOpen,
		})
	}

	buildSubmissionText() {
		let submissionText = ''
		if (this.props.gameMode === settingsEnums.SETTING_GAME_MODE_SYNC) {
			submissionText = `Waiting for other climbers to answer…${
				this.state.hasJoinedLate ? " You'll join on the next round!" : ''
			}`
		} else {
			submissionText = 'Submitting your response'
		}
		return submissionText
	}

	onVolumeChange = (volume: any) => {
		this.props.audioUpdate(volume, false)
	}

	onMuteSelected = (muteNewStatus: boolean) => {
		this.props.audioUpdate(this.props.volume, muteNewStatus)
	}

	onAnimationsToggle = () => {
		this.props.animationsToggleUpdate(!this.props.areAnimationsEnabled)
	}

	render() {
		const playerViewPayload = this.props.playerData.view.payload

		const isCorrect = wasFullyCorrectAnswered(this.state.correctAnswers, this.state.selectedAnswersIDs) //The student fully selected all the CORRECT answers of the question.

		const isAnswered = playerViewPayload.state !== gameStates.WAITING //The student answered the question.

		const isQuestionAnsweredButNotFinished = isAnswered && !this.props.isFinished //more students need to answer (and countdown didn't ended yet)

		const shouldPauseGame =
			this.props.isPauseEnabledSetting ||
			this.props.isTimerDisabledSetting === settingsEnums.SETTING_TIMER_DISABLED_FROM_SESSION ||
			this.props.isTimerDisabledSetting === settingsEnums.SETTING_TIMER_DISABLED_FROM_CREATION

		const isGamePaused = shouldPauseGame && this.props.isFinished //all the students answered (and/or countdown ended)

		const isMultipleAnswer = this.state.correctAnswers && this.state.correctAnswers.length >= 2

		const elementsColors = this.props.skin.questions.elements
		const elementColor = elementsColors[this.props.activity.index % elementsColors.length]

		const gameSize = this.getGameSize(this.props.size)

		const shouldShowOutOfTimeBar =
			isAnswered && (!playerViewPayload.answersUids || !playerViewPayload.answersUids[0]) && !this.state.hasJoinedLate

		const isTimerHidden =
			this.props.isTimerDisabledSetting === settingsEnums.SETTING_TIMER_DISABLED_FROM_SESSION ||
			this.props.isTimerDisabledSetting === settingsEnums.SETTING_TIMER_DISABLED_FROM_CREATION

		const shouldShowGameTimer = this.state.countdownComplete && !this.props.isFinished && !this.state.hasJoinedLate && !isTimerHidden

		const shouldShowSubmitButton =
			this.props.gameMode === settingsEnums.SETTING_GAME_MODE_OWN_PACED &&
			(this.props.isTimerDisabledSetting === settingsEnums.SETTING_TIMER_DISABLED_FROM_SESSION ||
				this.props.isTimerDisabledSetting === settingsEnums.SETTING_TIMER_DISABLED_FROM_CREATION) &&
			this.state.countdownComplete

		const submitButtonAriaLabel = `Submit answer and continue to next question. Question ${this.props.activity.index + 1} of ${this.props.totalActivities}`

		const waitingMessage = this.buildSubmissionText()

		const audioPath = this.props.skin.audio ? this.props.skin.audio.path : 'audios/gameplay.mp3'

		const progressBarColor = this.props.skin.progressBar ? this.props.skin.progressBar.color : palette.primary

		const topInformationBackgroundColor = this.props.skin.topInformation
			? this.props.skin.topInformation.backgroundColor
			: ''

		const questionBarWaitingBarColor = this.props.skin.progressBar ? this.props.skin.progressBar.color : palette.primary

		const getOutOfTimeContent = () => {
			const correctAnswersTexts = this.state.correctAnswers.map(answer => {
				if (answer?.media?.type === AnswerType.TEXT) {
					const textMedia = answer.media as IGameAnswerMediaText
					return textMedia.payload.text
				}

				if (answer?.media?.type === AnswerType.IMAGE) {
					const answerAccessibility = this.props.accessibility?.[answer.uid]
					if (answerAccessibility?.altText) {
						return answerAccessibility.altText
					}
					return 'Answer image'
				}

				return 'Answer'
			})

			const answerLabel = correctAnswersTexts.length > 1 ? 'Correct Answers' : 'Correct Answer'
			// An answer may be plain or rich text, and a question can hold both kinds at once.
			const answersList = joinRichTextContent(correctAnswersTexts)

			return `Oh Bummer! ${answerLabel}: ${answersList}`
		}

		return (
			<Wrapper style={{ width: gameSize.gameWidth, height: gameSize.gameHeight }}>
				<QuestionHeader
					style={{
						backgroundColor: isGamePaused ? palette.grayLight2 : elementColor.primary,
						transition: isGamePaused ? 'background-color 1000ms linear 7s' : 'none',
					}}
				>
					<h2
						style={{
							transform: isGamePaused ? 'scale(0.5)' : 'scale(1)',
							position: 'relative',
							top: isGamePaused ? '-10px' : '0',
							transition: isGamePaused ? 'all 1000ms linear 7s' : 'none',
						}}
					>
						{`Question #${this.props.activity.index + 1} of ${this.props.totalActivities}`}
					</h2>

					{!!isGamePaused && <QuestionHeaderPausedText>Paused</QuestionHeaderPausedText>}
				</QuestionHeader>

				<GameContainer>
					<QuestionContainer style={{ width: gameSize.questionWidth }}>
						<Question
							key={this.props.activity.index}
							questionIndex={this.props.activity.index}
							data={this.props.activity.payload}
							onSelect={this.onAnswerSelected}
							isFinished={this.props.isFinished}
							isAnswered={isAnswered}
							skin={this.props.skin.questions}
							gameSize={gameSize}
							onQuestionImageSelect={this.toggleImagePreview}
							selectedAnswersIDs={this.state.selectedAnswersIDs}
							isMultipleAnswer={isMultipleAnswer}
							isCorrect={isCorrect}
							accessibility={this.props.accessibility}
							areAnimationsEnabled={this.props.areAnimationsEnabled}
							shouldShowSubmitButton={shouldShowSubmitButton}
							submitButtonAriaLabel={submitButtonAriaLabel}
							onManualSubmit={this.onManualSubmit}
							handleNextQuestionKeyDown={this.handleNextQuestionKeyDown}
						/>
					</QuestionContainer>

					<DashboardContainer style={{ width: gameSize.dashboardWidth, height: gameSize.dashboardHeight }}>
						<Dashboard
							isCorrect={isCorrect}
							players={this.props.players}
							scale={gameSize.scale}
							playerUid={this.props.playerData.deviceUid}
							questionIndex={this.props.activity.index}
							totalActivities={this.props.totalActivities}
							isQuestionFinished={this.props.isFinished}
							skin={this.props.skin.dashboard}
							currentPlayerRank={this.props.currentPlayerRank}
							sortedPlayers={this.props.sortedPlayers}
							areAnimationsEnabled={this.props.areAnimationsEnabled}
						/>

						{this.props.isFinished && shouldPauseGame && (
							<DashboardContainerPausedOverlay>
								<PausedOverlayContent>
									<PauseIcon />

									<PausedOverlayText>Game paused</PausedOverlayText>
								</PausedOverlayContent>
							</DashboardContainerPausedOverlay>
						)}
					</DashboardContainer>

					{shouldShowGameTimer && (
						<GameTimerContainer
							key={this.props.activity.index}
							style={{
								width: `${90 * gameSize.timerScale}px`,
								height: `${90 * gameSize.timerScale}px`,
								fontSize: `${40 * gameSize.timerScale}px`,
								lineHeight: `${90 * gameSize.timerScale}px`,
								right: gameSize.timerRight,
							}}
						>
							<Timer
								totalTime={this.props.totalTime}
								elapsedTime={this.props.elapsedTime}
								onTimeComplete={this.onTimeComplete}
								onTimeUpdate={this.onTimeUpdate}
							/>
						</GameTimerContainer>
					)}
					<GameControlsContainer>
						{shouldShowSubmitButton && (
							<FloatingButton>
								<Button 
									onClick={this.onManualSubmit}
									onKeyDown={this.handleNextQuestionKeyDown}
									aria-label={submitButtonAriaLabel}
									variant="tertiary"
									disabled={isAnswered}
								>Submit answer</Button>
							</FloatingButton>
						)}
						<AnimationsToggle
							enabled={this.props.areAnimationsEnabled}
							onToggle={this.onAnimationsToggle}
						/>
						{this.props.isSoundEnabledSetting && (
							<VolumeBar
								aria-label="Volume control"
								onVolumeChange={this.onVolumeChange}
								onMuteSelected={this.onMuteSelected}
								isMuted={this.props.isMuted}
								volume={this.props.volume}
								isStudentView={true}
								widthSize={this.props.size.width}
							></VolumeBar>
						)}
					</GameControlsContainer>
					{shouldShowOutOfTimeBar && (
						<TopInformationContainer role='alert'>
							<TopInformation
								content={getOutOfTimeContent()}
								type={'info'}
								backgroundColor={topInformationBackgroundColor}
							/>
						</TopInformationContainer>
					)}
				</GameContainer>
				{!this.state.countdownComplete && (
					<CountdownContainer>
						<Countdown key={this.props.activity.index} onComplete={this.onComplete} skin={this.props.skin.countdown} />
					</CountdownContainer>
				)}
				{this.props.isFinished && !shouldPauseGame && (
					<QuestionProgressBar
						style={{
							backgroundColor: progressBarColor,
						}}
					/>
				)}

				{isQuestionAnsweredButNotFinished && (
					<QuestionWaitingBar
						style={{
							backgroundColor: questionBarWaitingBarColor,
						}}
					>
						<LoadingContainer>
							<LoadingAnimation>
								<LoadingWheel />
							</LoadingAnimation>
							<span style={{ paddingLeft: '1.8%' }}>{waitingMessage}</span>
						</LoadingContainer>
					</QuestionWaitingBar>
				)}
				{this.state.isImagePreviewOpen && this.props.activity.payload.asset && (
					<ImagePreview
						onClose={this.toggleImagePreview}
						asset={this.props.activity.payload.asset}
					/>
				)}
				<AudioElement src={audioPath} />
			</Wrapper>
		)
	}
}

export default Game
